import React from 'react';
import Grid from '@material-ui/core/Grid';
import {
  CONTACT,
  CORP,
  PRIVACY_POLICY,
  TERMS_OF_SERVICE,
  BUSINESSES,
} from '../routing';
import T from 'i18n-react';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import MuiLink from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { darken } from '@material-ui/core/styles';

const getShortRegionName = (name) => name.replace(/[県府都]$/, '');

const useStyles = makeStyles((theme) => ({
  container: {
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  navGrid: {
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  contact: {
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: darken(
        theme.palette.common.white,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  business: {
    color: theme.palette.common.white,
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
  buttonGroup: {
    marginLeft: 'auto',
    width: '100%',
    height: '100%',
    maxWidth: 500,
  },
  button: {
    height: '100%',
    padding: '10px 3px',
    width: '100%',
    fontSize: theme.typography.pxToRem(14),
    [theme.breakpoints.up('sm')]: {
      width: 'auto',
      minWidth: 180,
      padding: '10px 20px',
    },
  },
  buttonLabel: {
    display: 'block',
    [theme.breakpoints.only('xs')]: {
      display: 'inline-flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
  },
  link: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(14),
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.common.white,
      textDecoration: 'none',
    },
  },
  navTitle: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(14),
    marginBottom: theme.spacing(1),
  },
  section: {
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(14),
    marginBottom: theme.spacing(1),
  },
  sectionLinkList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  },
  sectionLink: {
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(12),
    textDecoration: 'none',
    '&:hover': {
      color: theme.palette.common.white,
      textDecoration: 'underline',
    },
  },
}));

const LinkGridItem = ({ href, children, ...others }) => {
  const classes = useStyles();
  return (
    <Grid
      item
      component={MuiLink}
      href={href}
      className={classes.link}
      {...others}
    >
      {children}
    </Grid>
  );
};

const MapBlock = ({
  regions = [],
  categories = [],
}: {
  regions?: Array<Object>,
  categories?: Array<Object>,
}) => {
  const classes = useStyles();

  return (
    <div>
      <Grid
        container
        spacing={2}
        alignItems="stretch"
        className={classes.container}
        wrap="nowrap"
      >
        <Grid item xs={12} sm={6} style={{ margin: 'auto 0' }}>
          <Typography className={classes.navTitle}>
            {T.translate('footer.aboutTitle')}
          </Typography>
          <Grid container spacing={2} className={classes.navGrid}>
            <LinkGridItem href={CORP}>
              {T.translate('footer.corp')}
            </LinkGridItem>
            <LinkGridItem href={TERMS_OF_SERVICE}>
              {T.translate('footer.terms')}
            </LinkGridItem>
            <LinkGridItem href={PRIVACY_POLICY}>
              {T.translate('footer.privacy')}
            </LinkGridItem>
            <LinkGridItem
              href={process.env.NOTION_JOB_OPPORTUNITIES_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {T.translate('footer.jobOpportunities')}
            </LinkGridItem>
            <LinkGridItem
              href={process.env.ZENDESK_FAQ_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {T.translate('footer.faq')}
            </LinkGridItem>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} className={classes.buttonGroup}>
          <Grid
            container
            spacing={2}
            alignItems="stretch"
            justify="flex-end"
            wrap="nowrap"
          >
            <Grid item xs={6} sm="auto">
              <Button
                href={CONTACT}
                color="primary"
                className={clsx(classes.button, classes.contact)}
                disableElevation
              >
                {T.translate('footer.contact')}
              </Button>
            </Grid>
            <Grid item xs={6} sm="auto">
              <Button
                href={process.env.NOTION_MUNICIPALITY_URL}
                target="_blank"
                variant="contained"
                color="primary"
                disableElevation
                className={clsx(classes.button, classes.business)}
                classes={{ label: classes.buttonLabel }}
              >
                <span>{T.translate('footer.municipality')}</span>
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {T.translate('footer.regionSectionTitle')}
        </Typography>
        <div className={classes.sectionLinkList}>
          {regions.map((region) => (
            <MuiLink
              key={region.id}
              href={`${BUSINESSES}/${region.enName.toLowerCase()}`}
              className={classes.sectionLink}
            >
              {getShortRegionName(region.name)}
            </MuiLink>
          ))}
        </div>
      </div>

      <div className={classes.section}>
        <Typography className={classes.sectionTitle}>
          {T.translate('footer.categorySectionTitle')}
        </Typography>
        <div className={classes.sectionLinkList}>
          {categories.map((category) => (
            <MuiLink
              key={category.tag}
              href={`${BUSINESSES}/${category.tag}`}
              className={classes.sectionLink}
            >
              {category.name}
            </MuiLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MapBlock;
